<template>
    <div>
        <div class="d-flex align-center">
            <!-- deleteFab -->
            <v-fab-transition>
                <v-btn fab absolute left v-show="deleteFab" dark small text color="red darken-2">
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </v-fab-transition>

            <!-- card -->
            <v-card
              outlined
              rounded="lg"
              width="100%"
              class="customer-card mx-auto"
              v-ripple="{class:'customer-ripple'}"
              v-touch.sync="{
                start: (e) => { touchStart(e) },
                move: (e) => { touchMove(e) },
                end: (e) => { endOffset(e) }
              }"
              :style="swipeStyle"
              :disabled="disabled"
            >
                <!-- shop image -->
                <v-img
                    :src="imageSrc"
                    :aspect-ratio="2"
                    @click="$emit('on-click')"
                />

                <!-- header: شريط أبيض بهيئة pill يطفو فوق أسفل الصورة -->
                <div class="customer-card__header px-3" @click="$emit('on-click')">
                    <v-sheet
                        outlined
                        rounded="pill"
                        width="100%"
                        class="customer-card__bar d-flex align-center px-3 py-1"
                    >
                        <!-- grade: شارة دائرية -->
                        <v-avatar
                            v-if="workloadName"
                            size="24"
                            color="cyan lighten-5"
                            class="me-2"
                        >
                            <span class="customer-card__grade cyan--text text--darken-2">
                                {{ workloadName }}
                            </span>
                        </v-avatar>

                        <span class="text-subtitle-2 font-weight-bold primary--text">
                            {{ customer.name }}
                        </span>

                        <v-spacer/>

                        <v-chip
                            v-if="specialtiesName"
                            x-small
                            color="orange lighten-5"
                            text-color="orange darken-2"
                            class="flex-shrink-0 font-weight-medium"
                        >
                            {{ specialtiesName }}
                        </v-chip>
                    </v-sheet>
                </div>

                <!-- details -->
                <v-card-text class="px-3 pt-3 pb-2" @click="$emit('on-click')">
                    <!-- address -->
                    <div v-if="addressText" class="customer-card__row">
                        <span class="customer-card__label grey--text text--darken-1">
                            {{ $T('inputs.address') }}
                        </span>
                        <span class="customer-card__value font-weight-medium text--primary">{{ addressText }}</span>
                    </div>

                    <!-- visit notes, falling back to contact info -->
                    <div v-if="customer.visitNotes" class="customer-card__row">
                        <span class="customer-card__label grey--text text--darken-1">
                            {{ $t('inputs.visit-notes') }}
                        </span>
                        <span class="customer-card__value font-weight-medium text--primary">{{ customer.visitNotes }}</span>
                    </div>
                    <div v-else-if="contactInfoText" class="customer-card__row">
                        <span class="customer-card__label grey--text text--darken-1">
                            {{ $t('Customers.contact-info') }}
                        </span>
                        <span class="customer-card__value font-weight-medium text--primary">{{ contactInfoText }}</span>
                    </div>
                </v-card-text>

                <v-card-actions class="customer-card__actions px-3 pb-3 pt-0">
                    <v-btn
                        depressed
                        color="amber lighten-5"
                        class="flex-grow-1 rounded text-none text-caption amber--text text--darken-2"
                        @click="$emit('edit', customer.id)"
                    >
                        <v-icon size="18" class="me-1">mdi-square-edit-outline</v-icon>
                        {{$T('edit')}}
                    </v-btn>
                    <v-btn
                        depressed
                        color="red lighten-5"
                        class="flex-grow-1 rounded text-none text-caption red--text text--darken-1"
                        @click="$emit('delete', customer.id)"
                    >
                        <v-icon size="18" class="me-1">mdi-trash-can-outline</v-icon>
                        {{$T('delete')}}
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- EditFab -->
            <v-fab-transition>
                <v-btn fab absolute right v-show="EditFab" dark small text color="success darken-2">
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
            </v-fab-transition>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
	name: 'CustomerCard',

	props: {
		customer: {
			type: Object,
			required: true
		},

		disabled: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			touchSwipeOffset: 0,
			maxOffsetAllowed: 30,
			directionLockThreshold: 10,
			touchDirection: null,

			snackbar: false,
			message: null,

			deleteFab: false,
			EditFab: false,
		}
	},

	computed: {
		...mapGetters({
			getSpecialtyById: 'specialties/getSpecialtyById',
			getWorkloadById: 'workloads/getWorkloadById',
			getRegionById: 'regions/getRegionById',
		}),

		// the default shop image, falling back to the shared placeholder
		imageSrc() {
			return this.customer.images?.find(image => image.isDefault)?.imagePath ||
				require('@/assets/imgs/img-placeholder.png')
		},

		// the workload is the customer grade (A+, A, B ...), stored on the address
		workloadName() {
			const workloadId = this.customer.entityAddresses?.[0]?.workloadId
			return this.getWorkloadById(workloadId)?.name ||
				this.customer.entityAddresses?.[0]?.workLoad?.name
		},

		specialtiesName() {
			if (!this.customer.specialtyIds?.length) return null
			return this.customer.specialtyIds
				.map(id => this.getSpecialtyById(id)?.name)
				.filter(Boolean)
				.join(', ')
		},

		addressText() {
			const address = this.customer.entityAddresses?.[0]
			if (!address) return null

			let addressValue
			if (address.address === '-') addressValue = null
			else addressValue = address.address

			return [this.getRegionById(address.regionId)?.name, addressValue]
				.filter(Boolean)
				.join(' - ')
		},

		contactInfoText() {
			if (!this.customer.contactInfos?.length) return null
			return this.customer.contactInfos
				.map(contactInfo => contactInfo.value)
				.filter(Boolean)
				.join(', ')
		},

		swipeStyle() {
			return `transform: translateX(${this.touchSwipeOffset}px)`
		}
	},

	methods: {
		// touch methods
		touchStart(e) {
			this.touchDirection = null
		},

		endOffset(e) {
			this.touchDirection = null
			setTimeout(() => {
				if (this.touchSwipeOffset === this.maxOffsetAllowed) {
					this.$emit('delete', this.customer.id)
				} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
					this.$emit('edit', this.customer.id)
				}
				this.deleteFab = false
				this.EditFab = false
				this.touchSwipeOffset = 0
			}, 200)
		},

		touchMove(e) {
			const offsetX = e.touchmoveX - e.touchstartX
			const offsetY = e.touchmoveY - e.touchstartY

			// don't commit to a direction until the finger has moved enough to tell a scroll from a swipe
			if (!this.touchDirection) {
				if (Math.abs(offsetX) < this.directionLockThreshold && Math.abs(offsetY) < this.directionLockThreshold) return
				this.touchDirection = Math.abs(offsetX) > Math.abs(offsetY) ? 'horizontal' : 'vertical'
			}

			if (this.touchDirection === 'vertical') return

			this.touchSwipeOffset = offsetX > -this.maxOffsetAllowed && offsetX < this.maxOffsetAllowed ? offsetX : Math.sign(offsetX) * this.maxOffsetAllowed

			if (this.touchSwipeOffset === this.maxOffsetAllowed) {
				this.deleteFab = true
				this.EditFab = false
			} else if (this.touchSwipeOffset === -this.maxOffsetAllowed) {
				this.deleteFab = false
				this.EditFab = true
			} else {
				this.deleteFab = false
				this.EditFab = false
			}
		},
	}
}
</script>

<style lang="scss">
.customer-card{
    transition: transform .4s ease-in-out;
    overflow: hidden;
}
.customer-ripple{
    color: var(--v-primary-base);
}
/* يُسحب الشريط فوق أسفل الصورة ليظهر عائماً */
.customer-card__header{
    position: relative;
    margin-top: -1.5rem;
}
.customer-card__bar{
    min-height: 38px;
}
.customer-card__grade{
    font-size: .625rem;
    font-weight: 700;
    line-height: 1;
}
/* فاصل 12px بين الزرّين بدلاً من 8px الافتراضية */
.customer-card__actions{
    gap: .75rem;

    & > .v-btn.v-btn + .v-btn{
        margin-left: 0;
    }
}
.customer-card__row{
    display: flex;
    align-items: flex-start;
    gap: .75rem;
    margin-bottom: .25rem;
}
/*
    الخط العام في vuetify-overrides يضبط *{font-size:.9rem} فيطابق كل عنصر مباشرة،
    ويتجاوز الحجم الموروث، لذلك نحدّد الحجم على العناصر الحاملة للنص نفسها
*/
.customer-card__label,
.customer-card__value{
    font-size: .75rem;
}
.customer-card__label{
    flex: 0 0 auto;
}
.customer-card__actions .v-btn__content{
    font-size: .75rem;
}
</style>
